// LiveStreamBadge.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import {
  LIVESTREAM_TYPE_LIVE_BADGE,
  LIVESTREAM_TYPE_STATUS_BADGE,
  LIVESTREAM_TYPE_DOT,
  LIVESTREAM_CARD_STATUS_BADGE,
} from '../resource/userStatusIndicatorConstants.js';
import {
  ExclusivePrivateStatus,
  LivestreamMode,
} from '../resource/liveStreamConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { textColor, color } from '../style/variables.js';
import OpacityBreath from '../style/OpacityBreath.js';
import media from '../style/media.js';
import LivestreamDotResource from '../../img/ic_livestream.gif';
import LivestreamPublicResource from '../../img/ic_private_view_xs.svg';

const BadgeState = {
  EXCLUSIVE_PRIVATE: 'exclusive-private',
  PERFORMING: 'performing',
  CROWDFUNDING_FUND_RAISING: 'crowdfundingFundRaising',
  STREAMING: 'streaming',
};

const ExclusivePrivateFill = css`
  background-image: linear-gradient(
    90deg,
    ${color.tealBlue} 0%,
    ${color.campaignBlue} 48.44%,
    ${color.campaignPurple} 100%
  );
`;
const PerformingFill = css`
  background-image: linear-gradient(
    90deg,
    ${color.mvpPink_alpha_80} 0%,
    ${color.mvpGold_alpha_80} 100%
  );
`;
const crowdfundingFundRaisingFill = css`
  background-image: linear-gradient(
    135deg,
    ${color.gamePurple_alpha_80} 0%,
    ${color.gamePurpleDeep_alpha_80} 100%
  );
`;

const BadgeStateConfig = {
  [BadgeState.EXCLUSIVE_PRIVATE]: {
    cardTitleKey: 'title_stream_exclusive',
    cardFill: ExclusivePrivateFill,
    statusTitleKey: 'title_stream_exclusive',
    statusFill: ExclusivePrivateFill,
  },
  [BadgeState.PERFORMING]: {
    cardTitleKey: 'performing',
    cardFill: PerformingFill,
    statusTitleKey: 'badge_live_performing',
    statusFill: PerformingFill,
  },
  [BadgeState.CROWDFUNDING_FUND_RAISING]: {
    cardTitleKey: 'badge_live_crowdfunding_fund_raising',
    cardFill: crowdfundingFundRaisingFill,
    statusTitleKey: 'badge_live_crowdfunding_fund_raising',
    statusFill: crowdfundingFundRaisingFill,
  },
  [BadgeState.STREAMING]: {
    cardTitleKey: 'title_streaming',
    cardFill: css`
      background: ${color.giftSelectionGradient};
    `,
    statusTitleKey: 'badge_live_public',
    statusFill: css`
      background-image: linear-gradient(
        136deg,
        ${color.orchid_alpha_80} 3.79%,
        ${color.carmine_alpha_80} 48.03%,
        ${color.hotPink_alpha_80} 97.19%
      );
    `,
  },
};

export class LiveStreamBadge extends React.PureComponent {
  render() {
    const { type, mode, exclusivePrivateStatus, size, t, isFluid } = this.props;
    const isPerforming = [
      LivestreamMode.CROWDFUNDING_PERFORMING,
      LivestreamMode.PRIVATE,
    ].includes(mode);
    const isExclusivePrivatePerforming =
      mode === LivestreamMode.EXCLUSIVE &&
      exclusivePrivateStatus === ExclusivePrivateStatus.PERFORMING;
    const { cardTitleKey, cardFill, statusTitleKey, statusFill } =
      BadgeStateConfig[
        isExclusivePrivatePerforming
          ? BadgeState.EXCLUSIVE_PRIVATE
          : mode === LivestreamMode.CROWDFUNDING_FUND_RAISING
            ? BadgeState.CROWDFUNDING_FUND_RAISING
            : isPerforming
              ? BadgeState.PERFORMING
              : BadgeState.STREAMING
      ];

    switch (type) {
      case LIVESTREAM_TYPE_DOT:
        return (
          <LivestreamDot>
            <Dot
              src={
                typeof LivestreamDotResource === 'object'
                  ? LivestreamDotResource.src
                  : LivestreamDotResource
              }
              alt="live streaming"
              size={size}
            />
          </LivestreamDot>
        );
      case LIVESTREAM_TYPE_LIVE_BADGE:
        return <LiveTextBadge size={size}>LIVE</LiveTextBadge>;
      case LIVESTREAM_CARD_STATUS_BADGE:
        return (
          <LivestreamCardStatusBadge $fill={cardFill}>
            {t(cardTitleKey, { ns: TranslationNamespace.LIVESTREAM })}
          </LivestreamCardStatusBadge>
        );
      case LIVESTREAM_TYPE_STATUS_BADGE:
        return (
          <StreamTextBadge $isFluid={isFluid} $fill={statusFill}>
            <img src={LivestreamPublicResource} alt="streaming badge" />
            {t(statusTitleKey, { ns: TranslationNamespace.LIVESTREAM })}
          </StreamTextBadge>
        );
      default:
        return null;
    }
  }
}

LiveStreamBadge.propTypes = {
  type: PropTypes.string,
  mode: PropTypes.oneOf(Object.values(LivestreamMode)),
  exclusivePrivateStatus: PropTypes.oneOf(
    Object.values(ExclusivePrivateStatus)
  ),
  size: PropTypes.number,
  isFluid: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

LiveStreamBadge.defaultProps = {
  type: LIVESTREAM_TYPE_DOT,
  mode: LivestreamMode.FREE,
  isFluid: false,
  size: 0,
};

const LivestreamDot = styled.div`
  display: flex;
  align-items: center;
`;

const Dot = styled.img.attrs(({ size }) => ({
  width: `${size}px`,
  height: `${size}px`,
}))``;

const LiveTextBadge = styled.div.attrs(({ size }) => ({
  style: {
    lineHeight: `${size}px`,
  },
}))`
  border-radius: 2px;
  padding: 0px 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${textColor.white};
  white-space: nowrap;
  background-image: linear-gradient(
    117deg,
    ${color.orchid} 5%,
    ${color.carmine} 55%,
    ${color.hotPink}
  );
`;

const StreamTextBadge = styled.div`
  position: relative;
  padding: 0px 4px;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: ${color.neutral[100]};
  ${({ $fill }) => $fill};
  > img {
    ${OpacityBreath({ duration: 0.5 })};
    margin-right: 4px;
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
  ${media.tablet`
    font-size: 12px;
    >img {
      width: 12px;
      height: 12px;
    }
  `};
  ${({ $isFluid }) =>
    $isFluid
      ? css`
          display: flex;
        `
      : css`
          border-radius: 2px;
          display: inline-flex;
        `};
`;

const LivestreamCardStatusBadge = styled.div`
  ${({ $fill }) => $fill};
  border-top-right-radius: 8px;
  border-bottom-left-radius: 8px;
  padding: 0px 8px;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.75;
  color: ${color.neutral[100]};
  ${media.tablet`
    font-size: 12px;
    line-height: 1.8;
  `};
  > img {
    margin-right: 2px;
    width: 20px;
    height: 20px;
    ${media.tablet`
      width: 16px;
      height: 16px;
    `};
  }
`;

export default withTranslation('translation', {
  withRef: true,
})(LiveStreamBadge);
